import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureException } from '../lib/errorTracking';
import { isStaleChunkError, handleStaleChunk, hasReloadedForStaleChunk } from '../lib/staleChunk';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  // A stale-chunk failure (lazy route requested an old hashed chunk after a
  // redeploy) that we're recovering from with a one-time reload — show a quiet
  // "Reloading…" state instead of the alarming error UI.
  reloading: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, reloading: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // If this is a stale-chunk error and we haven't already reloaded this
    // session, we're about to reload (triggered in componentDidCatch) — render
    // the "Reloading…" placeholder rather than the generic error fallback.
    const reloading = isStaleChunkError(error.message) && !hasReloadedForStaleChunk();
    return { hasError: true, error, reloading };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Stale code-split chunk after a redeploy: React.lazy's import rejection is
    // re-thrown into here (it never surfaces as a window error, so main.tsx's
    // listeners can't see it). Recover with one guarded reload and suppress the
    // GlitchTip report — it's a deploy-transient, self-healing condition, not a
    // real bug. If a reload was already attempted (handleStaleChunk returns
    // false), the chunk is genuinely gone → fall through to log + report.
    if (isStaleChunkError(error.message)) {
      if (handleStaleChunk()) return;
    }
    console.error('[ErrorBoundary]', error, info.componentStack);
    captureException(error, {
      contexts: { react: { componentStack: info.componentStack ?? undefined } },
    });
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // Stale-chunk recovery in flight — a full reload is imminent. Show a quiet
    // placeholder instead of the "Something went wrong" UI so the redeploy
    // transition is invisible to the user.
    if (this.state.reloading) {
      return (
        <section
          role="status"
          aria-live="polite"
          className="mx-auto flex min-h-[60vh] max-w-2xl items-center justify-center px-6 py-20"
        >
          <p className="font-body text-base text-text-gray">Loading the latest version…</p>
        </section>
      );
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <section
        role="alert"
        aria-labelledby="error-boundary-heading"
        className="mx-auto flex min-h-[60vh] max-w-2xl flex-col items-start justify-center px-6 py-20 lg:px-16"
      >
        <h1
          id="error-boundary-heading"
          className="mb-4 font-heading text-3xl font-bold text-text-primary"
        >
          Something went wrong
        </h1>
        <p className="mb-8 text-base leading-relaxed text-text-gray">
          An unexpected error occurred while rendering this page. You can try again or reload the
          page. If the problem persists, please contact support.
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={this.handleReset}
            className="rounded-lg bg-text-primary px-5 py-2.5 text-sm font-semibold text-surface-elevated transition-colors hover:bg-text-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-bg)]"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={this.handleReload}
            className="rounded-lg border border-surface-border-gray bg-surface-elevated px-5 py-2.5 text-sm font-semibold text-text-primary transition-colors hover:bg-surface-gray focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-bg)]"
          >
            Reload page
          </button>
        </div>
      </section>
    );
  }
}

export default ErrorBoundary;
